import React, { useMemo, useState } from 'react';

import { theme } from '~/theme';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { GridColDef, GridColumnVisibilityModel, GridExpandMoreIcon, GridPaginationModel } from '@mui/x-data-grid';

import CopyableField from '~/components/Common/CopyableField';
import GridPagination from '~/components/Common/Elements/GridPagination';
import PageSubheader from '~/components/Common/Elements/PageSubheader';
import Spinner from '~/components/Common/Spinner';
import DebugModal from '~/components/DebugModal/DebugModal';
import Table from '~/components/v2/common/Table/Table';

import { useListVoucherLogs } from '~/hooks/apis/promo/useListVoucherLogs';

import { ListVoucherLogsQuery, PromoRequestFilters, VoucherLog } from '~/services/PromoService';

import currencyFormatter from '~/utils/currencyFormatter';
import DateFormatterDetailed from '~/utils/dateFormatterDetails';

import ColumnSelector from '../PromoTester/ColumnSelector';
import PromoCodeUser from '../formatters/PromoCodeUser';

interface PromoRequestVoucherLogsProps {
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  voucherNames?: string[];
  userId?: string;
  brand?: string;
  promoRequestsFilter?: PromoRequestFilters;
  setPromoRequestsFilter?: (filter: PromoRequestFilters) => void;
  updateUrlState?: (filter: {
    userId?: string;
    voucherName?: string;
    expanded?: boolean;
    itemsExpanded?: boolean;
  }) => void;
  setFormUserId?: (userId: string) => void;
  setFormVoucherName?: (voucherName: string) => void;
}

const sharedAttrs: Pick<GridColDef, 'sortable' | 'display' | 'flex'> = {
  sortable: false,
  display: 'flex',
  flex: 1,
};

const DEFAULT_PAGE_SIZE = 20;

function PromoRequestVoucherLogs({
  expanded,
  onExpandedChange,
  voucherNames,
  userId,
  brand,
  promoRequestsFilter,
  setPromoRequestsFilter,
  updateUrlState,
  setFormUserId,
  setFormVoucherName,
}: PromoRequestVoucherLogsProps) {
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<GridColumnVisibilityModel>({
    voucherDescription: false,
    voucherToggles: false,
    voucherUserId: false,
    deletedAt: false,
    createdBy: false,
  });

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  const handleColumnVisibilityChange = (field: string, visible: boolean) => {
    setColumnVisibilityModel((prev) => ({
      ...prev,
      [field]: visible,
    }));
  };

  const handleFilterByVoucherName = (voucherName: string) => {
    updateUrlState?.({ voucherName });
    setPromoRequestsFilter?.({ ...promoRequestsFilter, voucherName });
    setFormVoucherName?.(voucherName);
  };

  const query = useMemo<ListVoucherLogsQuery>(() => {
    const q: ListVoucherLogsQuery = {
      page: paginationModel.page,
      limit: paginationModel.pageSize,
      sortBy: 'created_at',
      sortByDirection: 'desc',
    };
    if (voucherNames?.length) {
      q.voucherNames = voucherNames;
    }
    if (userId) {
      q.userId = userId;
    }
    if (brand === 'luxuryescapes') {
      q.brand = 'luxuryescapes';
    }
    return q;
  }, [paginationModel, voucherNames, userId, brand]);

  const { data, isLoading } = useListVoucherLogs({ query });

  const rows = data?.result?.logs ?? [];
  const total = Number(data?.result?.total ?? 0);

  const columns: Array<GridColDef<VoucherLog>> = [
    {
      field: 'createdAt',
      headerName: 'Created At',
      ...sharedAttrs,
      renderCell: (params) => <DateFormatterDetailed date={params.row.createdAt} />,
    },
    {
      field: 'voucherName',
      headerName: 'Voucher Name',
      ...sharedAttrs,
      flex: 2,
      valueGetter: (_value, row) => row.metadata?.voucherName ?? row.metadata?.voucherTitle ?? '',
      renderCell: (params) => {
        const name = params.row.metadata?.voucherName || params.row.metadata?.voucherTitle;
        if (!name) return null;
        return (
          <Stack>
            <Typography
              variant="body2"
              fontWeight="bold"
              sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
              onClick={() => handleFilterByVoucherName(name)}
              title="Click to filter by this voucher name"
            >
              {name}
            </Typography>
            <CopyableField
              value={params.row.voucherId}
              component="div"
              sx={{ display: 'flex', alignItems: 'center', minWidth: 0, width: '100%' }}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
                  <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {params.row.voucherId}
                  </Box>
                  <Tooltip title="Open voucher">
                    <IconButton
                      component={Link}
                      href={`/marketing/vouchers/${params.row.voucherId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      size="small"
                      sx={{ p: 0.25, ml: 0.5, flexShrink: 0 }}
                    >
                      <OpenInNewIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              }
            />
          </Stack>
        );
      },
    },
    {
      field: 'userId',
      headerName: 'User',
      ...sharedAttrs,
      renderCell: (params) =>
        params.row.userId ? (
          <PromoCodeUser
            userId={params.row.userId}
            userIdFor={params.row.userId}
            onUserIconClick={() => {
              updateUrlState?.({ userId: params.row.userId, expanded: true, itemsExpanded: true });
              setPromoRequestsFilter?.({ ...promoRequestsFilter, userId: params.row.userId });
              setFormUserId?.(params.row.userId);
            }}
          />
        ) : null,
    },
    {
      field: 'fkOrder',
      headerName: 'Order ID',
      ...sharedAttrs,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
          <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{params.row.fkOrder}</Box>
          <Tooltip title="Open order">
            <IconButton
              component={Link}
              href={`/purchases/${params.row.fkOrder}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              size="small"
              sx={{ p: 0.25, ml: 0.5, flexShrink: 0 }}
            >
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: 'amount',
      headerName: 'Amount Applied',
      ...sharedAttrs,
      renderCell: (params) => {
        const currency = params.row.metadata?.voucherCurrency || params.row.currencyCode;
        const remaining = params.row.metadata?.remainingAmountAfter;
        return (
          <Typography color="green">
            {currencyFormatter(currency, params.row.amount)}
            {remaining != null && remaining > 0 ? ` (${currencyFormatter(currency, remaining)} left)` : ''}
          </Typography>
        );
      },
    },
    {
      field: 'remainingAmountAfter',
      headerName: 'Remaining After',
      ...sharedAttrs,
      valueGetter: (_value, row) => row.metadata?.remainingAmountAfter,
      renderCell: (params) => {
        const currency = params.row.metadata?.voucherCurrency || params.row.currencyCode;
        return <Typography>{currencyFormatter(currency, params.row.metadata?.remainingAmountAfter)}</Typography>;
      },
    },
    {
      field: 'currencyCode',
      headerName: 'Currency',
      ...sharedAttrs,
      flex: 0,
    },
    {
      field: 'expiresAt',
      headerName: 'Expires At',
      ...sharedAttrs,
      valueGetter: (_value, row) => row.metadata?.expiredAt,
      renderCell: (params) =>
        params.row.metadata?.expiredAt ? <DateFormatterDetailed date={params.row.metadata.expiredAt} /> : null,
    },
    {
      field: 'createdBy',
      headerName: 'Created By',
      ...sharedAttrs,
      renderCell: (params) => <PromoCodeUser userId={params.row.createdBy} userIdFor={params.row.createdBy} />,
    },
    {
      field: 'voucherUserId',
      headerName: 'Voucher User ID',
      ...sharedAttrs,
      minWidth: 200,
    },
    {
      field: 'deletedAt',
      headerName: 'Deleted At',
      ...sharedAttrs,
      renderCell: (params) =>
        params.row.deletedAt ? (
          <DateFormatterDetailed date={params.row.deletedAt} />
        ) : (
          <Typography variant="body2">Active</Typography>
        ),
    },
    {
      field: 'voucherDescription',
      headerName: 'Description',
      ...sharedAttrs,
      valueGetter: (_value, row) => row.metadata?.voucherDescription,
    },
    {
      field: 'voucherToggles',
      headerName: 'Toggles',
      ...sharedAttrs,
      valueGetter: (_value, row) => row.metadata?.voucherToggles,
      renderCell: (params) => (
        <Typography variant="body2">{params.row.metadata?.voucherToggles?.join(', ')}</Typography>
      ),
    },
    {
      field: 'debug',
      headerName: '',
      ...sharedAttrs,
      flex: 0,
      renderCell: (params) => (
        <DebugModal
          type="generic"
          data={params.row}
          title={`Voucher ${params.row.metadata?.voucherName ?? params.row.voucherId}`}
        />
      ),
    },
  ];

  if (isLoading && !rows.length) {
    return <Spinner size={36} />;
  }

  return (
    <Accordion expanded={expanded} onChange={(_, expanded) => onExpandedChange?.(expanded)}>
      <AccordionSummary
        expandIcon={<GridExpandMoreIcon />}
        aria-controls="voucher-logs"
        id="voucher-logs"
        sx={{
          backgroundColor: theme.palette.grey[200],
        }}
      >
        <PageSubheader title={`Voucher Logs (${total})`} />
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ position: 'relative' }}>
          <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}>
            <ColumnSelector
              columns={columns}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityChange={handleColumnVisibilityChange}
            />
          </Box>

          <Table
            columns={columns}
            rows={rows}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            totalRowCount={total}
            isLoading={isLoading}
            getRowId={(row: VoucherLog) => row.voucherLogId}
            dataGridProps={{
              getRowHeight: () => 'auto',
              columnVisibilityModel,
              onColumnVisibilityModelChange: setColumnVisibilityModel,
              pageSizeOptions: [10, 20, 50, 100],
              slots: { pagination: GridPagination },
            }}
          />
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

export default PromoRequestVoucherLogs;
